import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useShareholdersMeetingsSuspenseQuery, ShareholdersMeetingsDocument } from './ShareholdersMeetings.graphql';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Dropdown } from '@startuptools/ui/Dropdown';
import { MenuButton } from '@startuptools/ui/MenuButton';
import { Menu } from '@startuptools/ui/Menu';
import { MenuItem } from '@startuptools/ui/MenuItem';
import { Trans, useLingui } from '@lingui/react';
import { MeetingHeldKindHuman } from '../meetings/components/MeetingHeldKindHuman';
import { MeetingStatusHuman } from '../meetings/components/MeetingStatusHuman';
import { MeetingKindHuman } from '../meetings/components/MeetingKindHuman';
import { GqlMeetingKind } from '../../graphql/base-types.graphql';
import { startTransition, useState } from 'react';
import { useCreateMeetingMutation, useDeleteMeetingMutation } from '../../graphql/react-operations';
import { meetingLink, meetingLinkEdit, meetingLinkShow, meetingsLink } from '../../components/Links';
import { CreateMeetingDialog } from '../common/components/meeting/CreateMeetingDialog';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { AlertConfirmDialog } from '../../components/simple-dialogs/AlertConfirmDialog';
import { ItemList } from '@startuptools/ui/ItemList';
import { usePaginatedQuery } from '@startuptools/ui/custom/UsePaginatedQuery';
import { Route, Routes } from 'react-router-dom';
import { ShareholdersMeetingRoutes } from './ShareholdersMeeting';
import { useNavigate } from '../../components/Navigate';

export const ShareholdersMeetingsRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<ShareholdersMeetings />} />
      <Route path="*" element={<ShareholdersMeetingRoutes />} />
    </Routes>
  );
};

export const ShareholdersMeetings = () => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { navigate } = useNavigate();
  const { link } = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState<{ id: string; kind: GqlMeetingKind } | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState<{
    usesStepper: boolean;
    kind: GqlMeetingKind.ExtraShareholdersMeeting | GqlMeetingKind.ShareholdersMeeting;
  } | null>(null);

  const {
    data: { meetings },
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
  } = usePaginatedQuery(useShareholdersMeetingsSuspenseQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { companyId },
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteMeetingMutation({ refetchQueries: [ShareholdersMeetingsDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreate] = useCreateMeetingMutation();

  return (
    <>
      <Stack gap={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography level="h2">
            <Trans id="Shareholders Meetings" />
          </Typography>
          <Box>
            <Stack direction="row" gap={1}></Stack>
          </Box>
        </Stack>

        <ItemList
          globalActionsChildren={
            <>
              <Dropdown>
                <MenuButton slots={{ root: Button }} slotProps={{ root: { variant: 'outlined' } }} color="neutral">
                  <Trans id="Upload Old Meeting" />
                </MenuButton>
                <Menu>
                  <MenuItem
                    onClick={() =>
                      setShowCreateDialog({ kind: GqlMeetingKind.ExtraShareholdersMeeting, usesStepper: false })
                    }
                  >
                    <Trans id="Extra general meeting" />
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      setShowCreateDialog({ kind: GqlMeetingKind.ShareholdersMeeting, usesStepper: false })
                    }
                  >
                    <Trans id="Annual general meeting" />
                  </MenuItem>
                </Menu>
              </Dropdown>
              <Dropdown>
                <MenuButton slots={{ root: Button }} color="primary">
                  <Trans id="New Meeting" />
                </MenuButton>
                <Menu>
                  <MenuItem
                    onClick={() =>
                      setShowCreateDialog({ kind: GqlMeetingKind.ExtraShareholdersMeeting, usesStepper: true })
                    }
                  >
                    <Trans id="Extra general meeting" />
                  </MenuItem>
                  <MenuItem
                    onClick={() => setShowCreateDialog({ kind: GqlMeetingKind.ShareholdersMeeting, usesStepper: true })}
                  >
                    <Trans id="Annual general meeting" />
                  </MenuItem>
                </Menu>
              </Dropdown>
            </>
          }
          linkTo={m => link(meetingLink(m))}
          onDelete={({ id }) => {
            setShowDeleteDialog({ id, kind: meetings.models.find(m => m.id === id)!.kind });
          }}
          columns={[
            {
              header: <Trans id="Kind" />,
              render: m => <MeetingKindHuman>{m.kind}</MeetingKindHuman>,
            },
            {
              header: <Trans id="Date" />,
              render: m => <DateFormat locale={i18n.locale}>{m.startTime}</DateFormat>,
            },
            {
              header: <Trans id="Held" />,
              render: m => <MeetingHeldKindHuman>{m.heldKind}</MeetingHeldKindHuman>,
            },
            {
              header: <Trans id="Status" />,
              render: m => (m.usesStepper ? <MeetingStatusHuman>{m.status}</MeetingStatusHuman> : null),
            },
            {
              header: <Trans id="Todo" />,
              render: m => (
                <Stack direction="row">
                  {m.meetingTodos.reduce((sum, t) => sum + (t.todo.done ? 1 : 0), 0)}
                  {' / '}
                  {m.meetingTodos.reduce((sum, t) => sum + (!t.todo.done ? 1 : 0), 0)}
                </Stack>
              ),
            },
          ]}
          items={meetings.models.map(m => ({
            ...m,
            deletable: true,
          }))}
          pagination={{
            totalCount: meetings.totalCount,
            page,
            rowsPerPage,
            setPage: value => startTransition(() => setPage(value)),
            setRowsPerPage: value => startTransition(() => setRowsPerPage(value)),
          }}
        />
      </Stack>
      {showDeleteDialog && (
        <AlertConfirmDialog
          title={<Trans id="Remove Meeting" />}
          onClose={() => setShowDeleteDialog(null)}
          onConfirm={async () => {
            await doDelete({ variables: { companyId, input: { id: showDeleteDialog.id } } });
            await navigate(meetingsLink(showDeleteDialog.kind));
            setShowDeleteDialog(null);
          }}
          content={<Trans id="Are you sure you want to delete this meeting?" />}
        />
      )}
      {showCreateDialog && (
        <CreateMeetingDialog
          onClose={() => setShowCreateDialog(null)}
          meetingKind={showCreateDialog.kind}
          usesStepper={showCreateDialog.usesStepper}
          onSubmit={async ({ startTime, endTime, ...input }) => {
            try {
              const meeting = await doCreate({
                variables: {
                  companyId,
                  input: {
                    startTime: startTime.toString(),
                    endTime: endTime?.toString(),
                    ...input,
                    kind: showCreateDialog.kind,
                    usesStepper: showCreateDialog.usesStepper,
                  },
                },
              });
              if (showCreateDialog.usesStepper) {
                return navigate(meetingLinkEdit(meeting.data!.createMeeting));
              } else {
                return navigate(meetingLinkShow(meeting.data!.createMeeting));
              }
            } catch (e) {
              toast({
                title: <Trans id="Error!" />,
                severity: 'danger',
                description: <Trans id="Could not create meeting, please try again" />,
              });
              captureException(e);
            }
          }}
        />
      )}
    </>
  );
};
